import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Mail, Paperclip, Send, ArrowLeft, X, CheckCircle, AlertCircle, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const SendSupportEmail = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [emailData, setEmailData] = useState({
    recipientEmail: '',
    subject: '',
    message: ''
  });
  const [attachments, setAttachments] = useState([]);
  const [emailSent, setEmailSent] = useState(false);

  // Check authentication and fetch email templates on component mount
  useEffect(() => {
    const token = localStorage.getItem('customerAdminToken');
    if (!token) {
      navigate('/customer-admin/login');
      return;
    }
    fetchTemplates();
  }, [navigate]);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('customerAdminToken');
      const response = await fetch(`${API_BASE_URL}/api/customer-admin/support-email-templates`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch templates');
      }

      const data = await response.json();
      setTemplates(data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load email templates');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEmailData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
    
    if (templateId === 'custom') {
      // Reset to custom email
      setEmailData({
        recipientEmail: emailData.recipientEmail,
        subject: '',
        message: ''
      });
      return;
    }

    const template = templates.find(t => t.id === templateId);
    if (template) {
      setEmailData(prev => ({
        ...prev,
        subject: template.subject,
        message: template.content
      }));
      toast.success(`Template "${template.name}" loaded`);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setAttachments(prev => [...prev, ...files]);
    toast.success(`${files.length} file(s) attached`);
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
    toast.info('Attachment removed');
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();

    // Validation
    if (!emailData.recipientEmail) {
      toast.error('Please enter recipient email address');
      return;
    }

    if (!emailData.recipientEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (!emailData.subject) {
      toast.error('Please enter email subject');
      return;
    }

    if (!emailData.message) {
      toast.error('Please enter email message');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('customerAdminToken');
      
      // Create FormData
      const formData = new FormData();
      formData.append('recipient_email', emailData.recipientEmail);
      formData.append('subject', emailData.subject);
      formData.append('message', emailData.message);
      
      if (selectedTemplate && selectedTemplate !== 'custom') {
        formData.append('template_id', selectedTemplate);
      }
      
      // Append attachments
      attachments.forEach(file => {
        formData.append('files', file);
      });

      const response = await fetch(`${API_BASE_URL}/api/customer-admin/send-support-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send email');
      }

      const result = await response.json();
      
      setEmailSent(true);
      toast.success('Support email sent successfully!');
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setEmailData({
          recipientEmail: '',
          subject: '',
          message: ''
        });
        setAttachments([]);
        setSelectedTemplate('');
        setEmailSent(false);
      }, 3000);

    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(error.message || 'Failed to send email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate('/customer-admin/dashboard')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-green-600 to-emerald-600 rounded-full flex items-center justify-center">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                Send Support Email
              </h1>
              <p className="text-gray-600 mt-1">Send professional support emails to customers from support@netixfibernet.com</p>
            </div>
          </div>
        </div>

        {/* Success Message */}
        {emailSent && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-green-800">Support Email Sent Successfully!</h3>
              <p className="text-green-700 text-sm">Your email has been sent to {emailData.recipientEmail}</p>
            </div>
          </div>
        )}

        {/* Email Form */}
        <Card className="shadow-xl">
          <CardHeader className="bg-gradient-to-r from-green-600 to-emerald-600 text-white">
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Support Email Composer
            </CardTitle>
            <CardDescription className="text-gray-100">
              From: support@netixfibernet.com
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSendEmail} className="space-y-6">
              {/* Template Selection */}
              <div>
                <Label htmlFor="template" className="text-base font-semibold flex items-center gap-2">
                  <FileText className="w-4 h-4 text-green-600" />
                  Email Template
                </Label>
                <Select 
                  value={selectedTemplate} 
                  onValueChange={handleTemplateSelect}
                  disabled={loadingTemplates}
                >
                  <SelectTrigger className="mt-2 text-base">
                    <SelectValue placeholder={loadingTemplates ? "Loading templates..." : "Select a template or write custom email"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="custom">Custom Email (No Template)</SelectItem>
                    {templates.map(template => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-gray-500 mt-1">
                  Choose a pre-built template or create a custom email
                </p>
              </div>

              {/* Recipient Email */}
              <div>
                <Label htmlFor="recipientEmail" className="text-base font-semibold flex items-center gap-2">
                  <Mail className="w-4 h-4 text-green-600" />
                  Customer Email Address
                </Label>
                <Input
                  id="recipientEmail"
                  name="recipientEmail"
                  type="email"
                  value={emailData.recipientEmail}
                  onChange={handleInputChange}
                  placeholder="customer@example.com"
                  className="mt-2 text-base"
                  required
                />
                <p className="text-sm text-gray-500 mt-1">Enter the customer's email address</p>
              </div>

              {/* Subject */}
              <div>
                <Label htmlFor="subject" className="text-base font-semibold">
                  Email Subject
                </Label>
                <Input
                  id="subject"
                  name="subject"
                  type="text"
                  value={emailData.subject}
                  onChange={handleInputChange}
                  placeholder="Enter email subject"
                  className="mt-2 text-base"
                  required
                />
                <p className="text-sm text-gray-500 mt-1">
                  {selectedTemplate && selectedTemplate !== 'custom' 
                    ? 'Subject loaded from template (you can edit it)' 
                    : 'Enter a custom subject for your email'}
                </p>
              </div>

              {/* Message */}
              <div>
                <Label htmlFor="message" className="text-base font-semibold">
                  Email Content
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={emailData.message}
                  onChange={handleInputChange}
                  placeholder="Type your support message here..."
                  rows={16}
                  className="mt-2 text-base resize-none font-mono"
                  required
                />
                <p className="text-sm text-gray-500 mt-1">
                  {selectedTemplate && selectedTemplate !== 'custom'
                    ? 'Template content loaded (customize as needed)'
                    : 'Write your support message. Use variables like {customer_name}, {ticket_id}, etc.'}
                </p>
              </div>

              {/* File Attachments */}
              <div>
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Paperclip className="w-4 h-4 text-green-600" />
                  Attachments (Optional)
                </Label>
                
                <div className="mt-2">
                  <input
                    type="file"
                    id="file-upload"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('file-upload').click()}
                    className="flex items-center gap-2"
                  >
                    <Paperclip className="w-4 h-4" />
                    Add Attachment
                  </Button>
                </div>

                {attachments.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <p className="text-sm font-medium text-gray-700">
                      Selected Files ({attachments.length}):
                    </p>
                    {attachments.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg p-3"
                      >
                        <div className="flex items-center gap-3 flex-1">
                          <Paperclip className="w-4 h-4 text-gray-500" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {file.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatFileSize(file.size)}
                            </p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeAttachment(index)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Info Box */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-green-800">
                    <p className="font-semibold mb-1">Support Email Features:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>Sent from <strong>support@netixfibernet.com</strong></li>
                      <li>Professional template with Netix Fibernet logo</li>
                      <li>Support team branding and contact information</li>
                      <li>Company footer with all contact details</li>
                      <li>Help section encouraging customer engagement</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Send Button */}
              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/customer-admin/dashboard')}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Support Email
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Available Templates Preview */}
        {!loadingTemplates && templates.length > 0 && (
          <div className="mt-6 bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-green-600" />
              Available Templates ({templates.length})
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {templates.map(template => (
                <div 
                  key={template.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-green-500 hover:shadow-md transition-all cursor-pointer"
                  onClick={() => handleTemplateSelect(template.id)}
                >
                  <h4 className="font-semibold text-gray-900 mb-1">{template.name}</h4>
                  <p className="text-sm text-gray-600">{template.subject}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SendSupportEmail;
